import React, {useMemo, useState} from 'react';
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    MRT_Row,
    type MRT_SortingState,
    type MRT_TableOptions,
    useMaterialReactTable,
} from 'material-react-table';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    useCreatePrevCustElem,
    useDeletePrevCustElem,
    useGetCustElem,
    useGetPrevCustElemByPrev,
    useUpdatePrevCustElem
} from "./ServicesCE";
import {PrevCustElem} from "./types";
import PreventiviEditCEROW from "./PreventiviEditCEROW";

interface PreventiviCEProps {
  onClose: () => void;
  idprevtestaprop: number;
}

const PreventiviCE: React.FC<PreventiviCEProps> = ({ onClose, idprevtestaprop }) => {
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  const [codAreaCust, setCodAreaCust] = useState<string | undefined>();
  const [desAreaCust, setDesAreaCust] = useState<string | undefined>();
  const [codModCust, setCodModCust] = useState<string | undefined>();
  const [desModCust, setDesModCust] = useState<string | undefined>();
  const [codNodoAreaCust, setCodNodoAreaCust] = useState<string | undefined>();
  const [codNodoModCust, setCodNodoModCust] = useState<string | undefined>();
  const [seqAreaCust, setSeqAreaCust] = useState<number | undefined>();
  const [seqModCust, setSeqModCust] = useState<number | undefined>();



  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 15 });


  //  by defaultColumn = { minSize: 40, maxSize: 1000, size: 180 };  SIZE IN PIXELS

  const columns = useMemo<MRT_ColumnDef<PrevCustElem>[]>(() => [
    {
      accessorKey: 'id',
      header: 'Id',
      enableEditing: false,
      grow: false,
      size: 40,
    },
      {
            accessorKey: 'flgprevcons',
            header: 'P/C',
            size: 60,
            editVariant: 'select',
            editSelectOptions: [
              { label: 'Preventivo', value: 'P' },
              { label: 'Consuntivo', value: 'C' },
            ],
           muiEditTextFieldProps: {
              select: true,
              required: true,
            },
            Cell: ({ cell }) => {
              const value = cell.getValue<'P' | 'C'>();

              return (
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: value === 'P' ? 'green' : 'blue',
                  }}
                >
                  {value}
                </Typography>
              );
            },
          },
    {
      accessorKey: 'codareacust',
      header: 'Cod.Area',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.codareacust,
        helperText: validationErrors?.codareacust,
        onFocus: () => setValidationErrors({ ...validationErrors, codareacust: undefined }),
      },
      grow: false,
      size: 40,
    },
    {
      accessorKey: 'desareacust',
      header: 'Area',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.desareacust,
        helperText: validationErrors?.desareacust,
        onFocus: () => setValidationErrors({ ...validationErrors, desareacust: undefined }),
      },
      grow: false,
      size: 250,
    },
    {
      accessorKey: 'codmodcust',
      header: 'Cod.Mod.',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.codmodcust,
        helperText: validationErrors?.codmodcust,
        onFocus: () => setValidationErrors({ ...validationErrors, codmodcust: undefined }),
      },
      grow: false,
      size: 40,
    },
    {
      accessorKey: 'desmodcust',
      header: 'Modulo',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.desmodcust,
        helperText: validationErrors?.desmodcust,
        onFocus: () => setValidationErrors({ ...validationErrors, desmodcust: undefined }),
      },
      grow: false,
      size: 250,
    },
    {
      accessorKey: 'numanalisi',
      header: 'Analisi',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numanalisi,
        helperText: validationErrors?.numanalisi,
        onFocus: () => setValidationErrors({ ...validationErrors, numanalisi: undefined }),
      },
      grow: false,
      size: 40,
    },
    {
      accessorKey: 'numconf',
      header: 'Conf.',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numconf,
        helperText: validationErrors?.numconf,
        onFocus: () => setValidationErrors({ ...validationErrors, numconf: undefined }),
      },
      grow: false,
      size: 40,
    },
    {
      accessorKey: 'numform',
      header: 'Form.',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numform,
        helperText: validationErrors?.numform,
        onFocus: () => setValidationErrors({ ...validationErrors, numform: undefined }),
      },
      grow: false,
      size: 40,
    },
    {
      accessorKey: 'numtot',
      header: 'Totale',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numtot,
        helperText: validationErrors?.numtot,
        onFocus: () => setValidationErrors({ ...validationErrors, numtot: undefined }),
      },
     grow: false,
     size: 40,
    },
    {
      accessorKey: 'numprep',
      header: 'Prep.',
      muiEditTextFieldProps: {
        required: true,
        error: !!validationErrors?.numprep,
        helperText: validationErrors?.numprep,
        onFocus: () => setValidationErrors({ ...validationErrors, numprep: undefined }),
      },
     grow: false,
     size: 40,
    },

    {
      accessorKey: 'note',
      header: 'Note',
      muiEditTextFieldProps: {
        required: false,
        error: !!validationErrors?.note,
        helperText: validationErrors?.note,
        onFocus: () => setValidationErrors({ ...validationErrors, note: undefined }),
      },
      grow: false,
      size: 100,
    },

  ], [validationErrors]);

  const { data: { results = [], count } = {}, isError: isLoadingCustElemError, isFetching: isFetchingCustElem, isLoading: isLoadingCustElem } = useGetPrevCustElemByPrev(pagination, columnFilters, globalFilter, sorting, idprevtestaprop);

 const { data: CustElemApiResponse } = useGetCustElem();

 //console.log('CUSTELEM:', CustElemApiResponse)

  const { mutateAsync: createPrevCustElem, isPending: isCreatingPrevCustElem } = useCreatePrevCustElem();
  const { mutateAsync: updatePrevCustElem, isPending: isUpdatingPrevCustElem } = useUpdatePrevCustElem();
  const { mutateAsync: deletePrevCustElem, isPending: isDeletingPrevCustElem } = useDeletePrevCustElem();

  // const [currentRowId, setCurrentRowId] = useState('0');
  // const [isDialogEditOpen, setIsDialogEditOpen] = useState(false);

  const [currentRowId, setCurrentRowId] = useState<number | null>(null);
  const [isDialogEditOpen, setIsDialogEditOpen] = useState(false);

  // const openEditModal = (row: MRT_Row<PrevCustElem>) => {
  //      setIsDialogEditOpen(true);
  //      setCurrentRowId(row.id);
  //  };

  const openEditModal = (row: MRT_Row<PrevCustElem>) => {
  setCurrentRowId(Number(row.original.id)); // ✅ real DB id
  setIsDialogEditOpen(true);
};


  const handleCreateCustElem: MRT_TableOptions<PrevCustElem>['onCreatingRowSave'] = async ({ values, table }) => {
    const newValidationErrors = validateCustElem(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});

    values.idprevtesta = idprevtestaprop;
    values.id = 0;
    await createPrevCustElem(values);
    table.setCreatingRow(null); // exit creating mode
  };

  const handleSaveCustElem: MRT_TableOptions<PrevCustElem>['onEditingRowSave'] = async ({ values, table }) => {
    const newValidationErrors = validateCustElem(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});

    const parseNum = (val: any) => parseFloat((val ?? '0').toString().replace(',', '.')) || 0;

    const numanalisi = parseNum(values.numanalisi);
    const numconf = parseNum(values.numconf);
    const numform = parseNum(values.numform);
    const numtot = numanalisi + numconf + numform;

    // console.log('numtot:',numtot);

        const myPrevCustElem = {
          id: values.id,
          idprevtesta: values.idprevtesta,
          desareacust: values.desareacust,
          codareacust: values.codareacust,
          desmodcust: values.desmodcust,
          codmodcust: values.codmodcust,
          numanalisi: values.numanalisi,
          numconf: values.numconf,
          numform: values.numform,
          numtot: numtot,
          numprep: values.numprep,
          note: values.note,
          codnodoareacust: values.codnodoareacust,
          codnodomodcust: values.codnodomodcust,
          seqareacust: values.seqareacust,
          seqmodcust: values.seqmodcust,
          flgprevcons: values.flgprevcons,

        };

     await updatePrevCustElem(myPrevCustElem);

    // await updatePrevCustElem(values);

    table.setEditingRow(null); // exit editing mode
  };

  const openDeleteConfirmModal = (row: MRT_Row<PrevCustElem>) => {
    if (window.confirm('Are you sure you want to delete this cust elem?')) {
      deletePrevCustElem(Number(row.original.id));
    }
  };


  const table = useMaterialReactTable({
    columns,
    data: results,
    createDisplayMode: 'row',
    editDisplayMode: 'modal',
    enableEditing: true,
    enableColumnActions: false,
    // muiEditRowDialogProps: {   open: true,     maxWidth: "md",  },
    initialState: { density: 'compact', pagination: { pageSize: 1000, pageIndex: 0 } },
    getRowId: (row) => row.id,
    muiToolbarAlertBannerProps: isLoadingCustElemError ? { color: 'error', children: 'Error loading data' } : undefined,
    muiTableContainerProps: { sx: { minHeight: '500px' } },
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateCustElem,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveCustElem,

    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        {/*<Tooltip title="Edit">*/}
        {/*  <IconButton onClick={() => table.setEditingRow(row)}>*/}
        {/*    <EditIcon />*/}
        {/*  </IconButton>*/}
        {/*</Tooltip>*/}


       <Tooltip title="Modifica">
          <IconButton   onClick={() => openEditModal(row)} >
            <EditIcon />
          </IconButton>
        </Tooltip>



        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    // renderTopToolbarCustomActions: ({ table }) => (
    // <Box sx={{ display: 'flex', gap: '16px', padding: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
    //   <Button variant="contained" onClick={() => { table.setCreatingRow(true); }}> Nuovo </Button>
    //   <Button variant="contained" onClick={() => onClose()}>Chiudi</Button>
    // </Box>
    // ),

    state: {
      isLoading: isLoadingCustElem,
      isSaving: isCreatingPrevCustElem || isUpdatingPrevCustElem || isDeletingPrevCustElem,
      showAlertBanner: isLoadingCustElemError,
      showProgressBars: isFetchingCustElem,
    },
  });


 return (
    <Dialog onClose={onClose} open fullWidth maxWidth="xl">
     <DialogTitle>
  <Box display="flex" justifyContent="space-between" alignItems="center">

    <Typography variant="h5">
      Attività Aggiuntive Preventivo {idprevtestaprop}
    </Typography>

    <Typography
      sx={{
        fontSize: "13px",
        color: "#666",
        textAlign: "right",
        maxWidth: "500px",
      }}
    >
      <b>NB:</b> Non inserire le ore di PM in questa form ma utilizzare modifica preventivo.
    </Typography>

  </Box>
</DialogTitle>
      <DialogContent>
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '16px'}}>
          <Button variant="contained" onClick={() => {
            table.setCreatingRow(true);
          }}> Nuova Area </Button>

          <div style={{ marginLeft: '8px' }}>
          <Autocomplete
              style={{ minWidth: '300px', maxWidth: '400px', flex: 1 }} // Adjust the width here
              disablePortal
              size="small"
              id="idarea"
              getOptionLabel={(option) => option.desareacust || ""}
              value={CustElemApiResponse?.results.find((item) => item.codareacust === codAreaCust) || null}
              options={CustElemApiResponse?.results ?? []}
              onChange={(event, value) => {
                if (value !== null && value.codareacust !== undefined) {
                  setCodAreaCust(value.codareacust);
                  setDesAreaCust(value.desareacust);
                  setCodModCust(value.codmodcust);
                  setDesModCust(value.desmodcust);
                  setCodNodoAreaCust(value.codnodoareacust);
                  setCodNodoModCust(value.codnodomodcust);
                  setSeqAreaCust(value.seqareacust);
                  setSeqModCust(value.seqmodcust);

                } else {
                  console.log("No value selected");
                }
              }}
              renderInput={(params) => <TextField {...params} margin="dense" label="Seleziona Area"/>}
          />
          </div>
          <Button
              variant="contained"
              onClick={async () => {
                if (!desAreaCust) {
                  return;
                }
                const newPrevCustElem: Partial<PrevCustElem> = {codareacust: codAreaCust, desareacust: desAreaCust, codmodcust: codModCust, desmodcust: desModCust,
                  idprevtesta: idprevtestaprop,   codnodoareacust: codNodoAreaCust, codnodomodcust: codNodoModCust, seqareacust: seqAreaCust, seqmodcust: seqModCust, flgprevcons: 'P' };
                await createPrevCustElem(newPrevCustElem as PrevCustElem);
              }}
              style={{marginLeft: '8px'}}
          >
            AGGIUNGI
          </Button>
             <Button variant="contained" onClick={() => onClose()} style={{marginLeft: '8px'}}>Chiudi</Button>
        </div>
        <MaterialReactTable table={table}/>

         {isDialogEditOpen && currentRowId !== null && (
                          <PreventiviEditCEROW
                            open={isDialogEditOpen}
                            idprevcustelemprop={currentRowId}
                            onClose={() => {
                              setIsDialogEditOpen(false);
                              setCurrentRowId(null);
                            }}
                          />
                        )}


      </DialogContent>


    </Dialog>

 );

};

export default PreventiviCE;

const validateRequired = (value: string) => !!value.length;
// const validateNumber = (value: number) => !isNaN(value);

const validateNumber = (value: any): boolean => {
    // Check if the value is of type 'number' and is not NaN
    if (typeof value === 'number' && !isNaN(value)) {
        return true;
    }

    // If the value is a string, try to convert it to a number
    if (typeof value === 'string') {
        // Remove any commas in the string and replace with dots for proper parsing
        const normalizedValue = value.replace(',', '.');
        const parsedNumber = parseFloat(normalizedValue);
        return !isNaN(parsedNumber);
    }

    // If the value is neither a number nor a string, it's not valid
    return false;
};





const validateEmail = (email: string) =>
    !!email.length &&
    email.toLowerCase().match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );

function validateCustElem(prevcustelem: PrevCustElem) {
  return {
    numanalisi: !validateNumber(prevcustelem.numanalisi) ? 'Valore numerico' : '',
    numconf: !validateNumber(prevcustelem.numconf) ? 'Valore numerico' : '',
    numform: !validateNumber(prevcustelem.numform) ? 'Valore numerico' : '',
    numtot: !validateNumber(prevcustelem.numtot) ? 'Valore numerico' : '',
    numprep: !validateNumber(prevcustelem.numprep) ? 'Valore numerico' : '',



    // firstName: !validateRequired(user.firstName)
    //   ? 'First Name is Required'
    //   : '',
    // lastName: !validateRequired(user.lastName) ? 'Last Name is Required' : '',
    // email: !validateEmail(user.email) ? 'Incorrect Email Format' : '',



  };
}
